import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useEditor, EditorContent, Extension } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ResizableImage from '../components/ResizableImageExtension';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { DOMParser as PMMDOMParser } from '@tiptap/pm/model';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { ySyncPlugin, yCursorPlugin, yUndoPlugin } from 'y-prosemirror';
import api from '../utils/api';
import { getContrastColor } from '../utils/color';
import { useTheme } from '../contexts/ThemeContext';
import dayjs from 'dayjs';
import {
  ChevronLeft, ChevronRight, Search, X, WifiOff, CalendarDays,
  CheckSquare, AlertTriangle, ClipboardList, Plus, Trash2, Settings,
  Check, ChevronDown,
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import EditorToolbar from '../components/EditorToolbar';
import MarkdownPaste from '../extensions/MarkdownPaste';
import { markdownToHtml } from '../utils/markdownUtils';

const COLORS = ['#f87171','#fb923c','#facc15','#4ade80','#22d3ee','#818cf8','#e879f9','#f472b6'];
function getUserColor(id) { return COLORS[(id || 0) % COLORS.length]; }

function cursorBuilder(user) {
  const cursor = document.createElement('span');
  cursor.className = 'yjs-cursor';
  cursor.style.borderColor = user.color;
  const label = document.createElement('span');
  label.className = 'yjs-cursor-label';
  label.style.backgroundColor = user.color;
  label.textContent = user.name;
  cursor.appendChild(label);
  return cursor;
}

const DEPT_MAP = {
  product: '상품개발본부',
  material: '소재개발본부',
  equipment: '장비개발본부',
  management: '경영지원',
  sales: '영업',
};

const PRODUCT_SECTION_ORDER = ['이지안', '최형렬', '박양구', '김태연'];

const SECTIONS = [
  { key: 'today',    icon: CheckSquare,   label: '오늘 한 일', color: 'text-brand' },
  { key: 'issue',    icon: AlertTriangle, label: '이슈',       color: 'text-amber-400' },
  { key: 'tomorrow', icon: ClipboardList, label: '내일 계획',  color: 'text-gray-400' },
];

const PRESET_COLORS = ['#818cf8','#f87171','#4ade80','#fb923c','#22d3ee','#facc15','#e879f9','#f472b6','#a78bfa','#34d399'];

async function uploadImage(file) {
  const fd = new FormData();
  fd.append('image', file);
  const r = await api.post('/boards/upload', fd);
  return r.data.url;
}

function getKstToday() {
  return new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

// ── 공통 섹션 필드 정의 ─────────────────────────────────────────
const COMMON_FIELD_GROUPS = [
  {
    label: '신바나 진행상황',
    color: 'text-violet-400',
    fields: [
      { key: 'xinvana-test',   label: '테스트' },
      { key: 'xinvana-output', label: '출력' },
    ],
  },
  {
    label: '머천다이즈 진행 상황',
    color: 'text-emerald-400',
    fields: [
      { key: 'merch-req',    label: '출력 요청' },
      { key: 'merch-done',   label: '출력 완료' },
      { key: 'merch-weekly', label: '이번 주 굿즈 진행상황' },
    ],
  },
  {
    label: '메모',
    color: 'text-gray-400',
    fields: [
      { key: 'memo', label: '메모' },
    ],
  },
];

// 공통 섹션 단일 필드 체크리스트 (오늘한일/이슈/내일계획과 동일한 UX)
function CommonFieldSection({ fieldKey, label, date, department, items, canEdit, onItemsChange }) {
  const fieldItems = items.filter(i => i.fieldKey === fieldKey);
  const [addText, setAddText] = useState('');
  const [adding, setAdding] = useState(false);
  const addRef = useRef(null);

  const addItem = async () => {
    const trimmed = addText.trim();
    if (!trimmed) { setAdding(false); return; }
    const res = await api.post('/daily-work/common-fields', {
      date, department, fieldKey, text: trimmed, sortOrder: fieldItems.length,
    });
    onItemsChange(prev => [...prev, { ...res.data, fieldKey }]);
    setAddText('');
    addRef.current?.focus();
  };

  const updateItem = async (item, text) => {
    const res = await api.put(`/daily-work/common-fields/${item.id}`, { text });
    onItemsChange(prev => prev.map(i => i.id === item.id ? { ...res.data, fieldKey } : i));
  };

  const deleteItem = async (item) => {
    await api.delete(`/daily-work/common-fields/${item.id}`);
    onItemsChange(prev => prev.filter(i => i.id !== item.id));
  };

  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900 p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold text-gray-300">{label}</span>
          {fieldItems.length > 0 && <span className="text-[10px] text-gray-600">{fieldItems.length}</span>}
        </div>
        {canEdit && !adding && (
          <button onClick={() => { setAdding(true); setTimeout(() => addRef.current?.focus(), 50); }}
            className="text-[11px] px-2 py-0.5 rounded-md bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-200 transition-colors flex items-center gap-1">
            <Plus size={10} />작성
          </button>
        )}
      </div>
      <div className="space-y-0.5">
        {fieldItems.map(item => (
          <CommonFieldItem key={item.id} item={item} canEdit={canEdit}
            onTextChange={text => updateItem(item, text)}
            onDelete={() => deleteItem(item)} />
        ))}
      </div>
      {adding && canEdit && (
        <div className="flex items-center gap-2 mt-1 px-2">
          <span className="w-1.5 h-1.5 rounded-full bg-gray-600 flex-shrink-0" />
          <input ref={addRef} value={addText} onChange={e => setAddText(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); addItem(); }
              if (e.key === 'Escape') { setAdding(false); setAddText(''); }
            }}
            onBlur={() => { addItem(); setAdding(false); }}
            placeholder="항목 입력..."
            className="flex-1 bg-transparent border-b border-gray-700 text-sm text-white py-0.5 focus:outline-none focus:border-brand placeholder-gray-600" />
        </div>
      )}
      {fieldItems.length === 0 && !adding && !canEdit && (
        <p className="text-gray-700 text-xs text-center py-1">없음</p>
      )}
    </div>
  );
}

function CommonFieldItem({ item, canEdit, onTextChange, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.text);
  const inputRef = useRef(null);

  const save = () => {
    setEditing(false);
    if (text.trim() && text !== item.text) onTextChange(text.trim());
    else setText(item.text);
  };

  return (
    <div className="group flex items-start gap-2 px-2 py-1 rounded-lg hover:bg-gray-800/40">
      <span className="w-1.5 h-1.5 rounded-full bg-gray-600 flex-shrink-0 mt-1.5" />
      {editing ? (
        <input ref={inputRef} value={text} onChange={e => setText(e.target.value)}
          onBlur={save}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); save(); } if (e.key === 'Escape') { setText(item.text); setEditing(false); } }}
          className="flex-1 bg-transparent text-sm text-white focus:outline-none border-b border-gray-600" autoFocus />
      ) : (
        <span onClick={() => canEdit && (setEditing(true), setText(item.text))}
          className={`flex-1 text-sm text-gray-200 leading-snug ${canEdit ? 'cursor-text' : ''}`}>
          {item.text}
        </span>
      )}
      {canEdit && !editing && (
        <button onClick={onDelete} className="opacity-0 group-hover:opacity-100 text-gray-600 hover:text-red-400 transition-all flex-shrink-0">
          <Trash2 size={11} />
        </button>
      )}
    </div>
  );
}

function CommonSection({ date, department, canEdit }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!date || !department) return;
    api.get('/daily-work/common-fields', { params: { date, department } })
      .then(r => {
        const flat = [];
        Object.entries(r.data).forEach(([fieldKey, arr]) =>
          arr.forEach(item => flat.push({ ...item, fieldKey }))
        );
        setItems(flat);
      })
      .catch(() => {});
  }, [date, department]);

  return (
    <div className="p-4 border-b border-gray-800/50 flex flex-col gap-3 flex-shrink-0">
      <span className="text-[10px] text-gray-600 uppercase tracking-widest font-semibold">공통 현황</span>
      {COMMON_FIELD_GROUPS.map(group => (
        <div key={group.label}>
          <div className={`text-[11px] font-semibold mb-1.5 ${group.color}`}>{group.label}</div>
          <div className="flex flex-col gap-1.5 pl-1">
            {group.fields.map(f => (
              <CommonFieldSection key={f.key} fieldKey={f.key} label={f.label}
                date={date} department={department}
                items={items} canEdit={canEdit} onItemsChange={setItems} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── 카테고리 관리 모달 ────────────────────────────────────────────
function CategoryModal({ categories, department, onClose, onRefresh }) {
  const [form, setForm] = useState({ name: '', color: '#818cf8' });
  const [editing, setEditing] = useState(null);

  const save = async () => {
    if (!form.name.trim()) return;
    if (editing) await api.put(`/daily-work/categories/${editing.id}`, form);
    else await api.post('/daily-work/categories', { ...form, department });
    setEditing(null);
    setForm({ name: '', color: '#818cf8' });
    onRefresh();
  };

  const remove = async (id) => {
    if (!confirm('삭제하시겠습니까?')) return;
    await api.delete(`/daily-work/categories/${id}`);
    onRefresh();
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-bold text-sm">카테고리 관리</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-lg leading-none">×</button>
        </div>
        <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
          {categories.map(c => (
            <div key={c.id} className="flex items-center gap-2 bg-gray-800 rounded-lg px-3 py-2">
              <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: c.color }} />
              <span className="text-white text-sm flex-1">{c.name}</span>
              <button onClick={() => { setEditing(c); setForm({ name: c.name, color: c.color }); }}
                className="text-gray-500 hover:text-white p-1 text-xs">수정</button>
              <button onClick={() => remove(c.id)} className="text-gray-500 hover:text-red-400 p-1"><Trash2 size={12} /></button>
            </div>
          ))}
          {categories.length === 0 && <p className="text-gray-600 text-xs text-center py-2">등록된 카테고리가 없습니다.</p>}
        </div>
        <div className="border-t border-gray-800 pt-3">
          <p className="text-gray-400 text-xs mb-2">{editing ? '카테고리 수정' : '카테고리 추가'}</p>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="카테고리 이름" onKeyDown={e => e.key === 'Enter' && save()}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-brand mb-2" />
          <div className="flex items-center gap-1.5 mb-3 flex-wrap">
            {PRESET_COLORS.map(c => (
              <button key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
                className={`w-5 h-5 rounded-full border-2 transition-transform ${form.color === c ? 'border-white scale-125' : 'border-transparent hover:scale-110'}`}
                style={{ backgroundColor: c }} />
            ))}
          </div>
          <div className="flex gap-2">
            {editing && (
              <button onClick={() => { setEditing(null); setForm({ name: '', color: '#818cf8' }); }}
                className="px-3 py-1.5 text-gray-400 text-sm hover:text-white">취소</button>
            )}
            <button onClick={save} className="px-4 py-1.5 bg-brand text-white text-sm rounded-lg hover:bg-brand/80">
              {editing ? '수정' : '추가'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── 인라인 카테고리 선택 드롭다운 ─────────────────────────────────
function CategoryPicker({ categories, value, onChange, onClose, above }) {
  return (
    <div className={`absolute z-30 ${above ? 'bottom-full mb-1' : 'top-full mt-1'} left-0 bg-gray-800 border border-gray-700 rounded-lg shadow-xl min-w-[120px] py-1`}
      onClick={e => e.stopPropagation()}>
      <button className="w-full text-left px-3 py-1.5 text-xs text-gray-400 hover:bg-gray-700 hover:text-white"
        onClick={() => { onChange(null); onClose(); }}>
        없음
      </button>
      {categories.map(c => (
        <button key={c.id} onClick={() => { onChange(c.id); onClose(); }}
          className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-700 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: c.color }} />
          <span className="text-white">{c.name}</span>
          {value === c.id && <Check size={10} className="ml-auto text-brand" />}
        </button>
      ))}
    </div>
  );
}

// ── 업무 항목 ─────────────────────────────────────────────────────
function WorkItem({ item, categories, canEdit, onTextChange, onNotesChange, onCategoryChange, onDelete }) {
  const [editingText, setEditingText] = useState(false);
  const [text, setText] = useState(item.text);
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(item.notes || '');
  const [showCatPicker, setShowCatPicker] = useState(false);
  const [catPickerAbove, setCatPickerAbove] = useState(false);
  const catBtnRef = useRef(null);
  const textInputRef = useRef(null);
  const notesRef = useRef(null);
  const notesTimer = useRef(null);

  const commitText = () => {
    setEditingText(false);
    const trimmed = text.trim();
    if (trimmed && trimmed !== item.text) onTextChange(trimmed);
    else setText(item.text);
  };

  const handleNotesChange = (val) => {
    setNotes(val);
    clearTimeout(notesTimer.current);
    notesTimer.current = setTimeout(() => onNotesChange(val), 600);
  };

  const handleRowClick = (e) => {
    if (e.target.closest('button') || e.target.closest('input') || editingText) return;
    setExpanded(v => {
      if (!v) setTimeout(() => notesRef.current?.focus(), 50);
      return !v;
    });
  };

  useEffect(() => { if (editingText) textInputRef.current?.focus(); }, [editingText]);
  useEffect(() => { setText(item.text); }, [item.text]);
  useEffect(() => { setNotes(item.notes || ''); }, [item.notes]);

  const cat = categories.find(c => c.id === item.categoryId) || item.category;

  return (
    <div className="group">
      <div
        onClick={handleRowClick}
        className="flex items-center gap-2 py-1 px-2 rounded-lg hover:bg-white/5 transition-colors relative cursor-pointer">
        {/* 불릿 */}
        <span className="relative flex-shrink-0 mt-0.5">
          <span className="block w-1.5 h-1.5 rounded-full bg-gray-600" />
          {notes && <span className="absolute -top-0.5 -right-0.5 w-1 h-1 rounded-full bg-brand" />}
        </span>

        {/* 텍스트 */}
        {editingText && canEdit ? (
          <input
            ref={textInputRef}
            value={text}
            onChange={e => setText(e.target.value)}
            onBlur={commitText}
            onKeyDown={e => {
              if (e.key === 'Enter') commitText();
              if (e.key === 'Escape') { setText(item.text); setEditingText(false); }
            }}
            className="flex-1 bg-gray-800 border border-gray-700 rounded px-2 py-0.5 text-sm text-white focus:outline-none focus:border-brand"
          />
        ) : (
          <span
            onDoubleClick={() => canEdit && setEditingText(true)}
            className="flex-1 text-sm text-gray-200 select-none">
            {item.text}
          </span>
        )}

        {/* 카테고리 태그 */}
        <div className="relative flex-shrink-0">
          <button
            ref={catBtnRef}
            onClick={() => {
              if (!canEdit) return;
              if (!showCatPicker && catBtnRef.current) {
                const rect = catBtnRef.current.getBoundingClientRect();
                setCatPickerAbove(rect.bottom > window.innerHeight - 160);
              }
              setShowCatPicker(v => !v);
            }}
            className={`text-[10px] px-1.5 py-0.5 rounded-full ${canEdit ? 'hover:opacity-80' : ''}`}
            style={cat ? { backgroundColor: cat.color + '33', color: cat.color } : { color: '#6b7280' }}>
            {cat ? cat.name : (canEdit ? '+ 카테고리' : '')}
          </button>
          {showCatPicker && canEdit && (
            <CategoryPicker
              categories={categories}
              value={item.categoryId}
              onChange={catId => onCategoryChange(catId)}
              onClose={() => setShowCatPicker(false)}
              above={catPickerAbove}
            />
          )}
        </div>

        {/* 메모 토글 */}
        <button
          onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
          className={`transition-all flex-shrink-0 ${expanded ? 'text-gray-400 rotate-180' : 'opacity-0 group-hover:opacity-100 text-gray-600 hover:text-gray-400'}`}>
          <ChevronDown size={12} />
        </button>

        {/* 삭제 */}
        {canEdit && (
          <button onClick={e => { e.stopPropagation(); onDelete(); }}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-600 hover:text-red-400 flex-shrink-0">
            <X size={12} />
          </button>
        )}
      </div>

      {/* 메모 영역 */}
      {expanded && (
        <div className="pl-5 pr-2 pb-1">
          <textarea
            ref={notesRef}
            value={notes}
            onChange={e => handleNotesChange(e.target.value)}
            readOnly={!canEdit}
            placeholder={canEdit ? '추가 내용을 입력하세요...' : ''}
            rows={2}
            className="w-full bg-gray-800/60 border border-gray-700/50 rounded-lg px-3 py-2 text-xs text-gray-300 focus:outline-none focus:border-brand/50 resize-none placeholder-gray-600"
          />
        </div>
      )}
    </div>
  );
}

// ── 섹션별 체크리스트 ─────────────────────────────────────────────
function CheckSection({ sectionDef, items, categories, canEdit, adminId, date, onItemsChange }) {
  const { key, icon: Icon, label, color } = sectionDef;
  const sectionItems = items.filter(i => i.section === key);
  const [addText, setAddText] = useState('');
  const [adding, setAdding] = useState(false);
  const addRef = useRef(null);

  const addItem = async () => {
    const trimmed = addText.trim();
    if (!trimmed) { setAdding(false); return; }
    const res = await api.post('/daily-work/items', {
      date, section: key, text: trimmed,
      adminId, sortOrder: sectionItems.length,
    });
    onItemsChange(prev => [...prev, res.data]);
    setAddText('');
    addRef.current?.focus();
  };

  const updateText = async (item, text) => {
    const res = await api.put(`/daily-work/items/${item.id}`, { text });
    onItemsChange(prev => prev.map(i => i.id === item.id ? res.data : i));
  };

  const updateNotes = async (item, notes) => {
    const res = await api.put(`/daily-work/items/${item.id}`, { notes });
    onItemsChange(prev => prev.map(i => i.id === item.id ? res.data : i));
  };

  const updateCategory = async (item, categoryId) => {
    const res = await api.put(`/daily-work/items/${item.id}`, { categoryId });
    onItemsChange(prev => prev.map(i => i.id === item.id ? res.data : i));
  };

  const deleteItem = async (item) => {
    await api.delete(`/daily-work/items/${item.id}`);
    onItemsChange(prev => prev.filter(i => i.id !== item.id));
  };

  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900 p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Icon size={13} className={color} />
          <span className={`text-xs font-semibold ${color}`}>{label}</span>
          {sectionItems.length > 0 && (
            <span className="text-[10px] text-gray-600">{sectionItems.length}</span>
          )}
        </div>
        {canEdit && !adding && (
          <button onClick={() => { setAdding(true); setTimeout(() => addRef.current?.focus(), 50); }}
            className="text-[11px] px-2 py-0.5 rounded-md bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-200 transition-colors flex items-center gap-1">
            <Plus size={10} />작성
          </button>
        )}
      </div>

      <div className="space-y-0.5">
        {sectionItems.map(item => (
          <WorkItem
            key={item.id}
            item={item}
            categories={categories}
            canEdit={canEdit}
            onTextChange={text => updateText(item, text)}
            onNotesChange={notes => updateNotes(item, notes)}
            onCategoryChange={catId => updateCategory(item, catId)}
            onDelete={() => deleteItem(item)}
          />
        ))}
      </div>

      {(adding && canEdit) && (
        <div className="flex items-center gap-2 mt-1 px-2">
          <span className="w-1.5 h-1.5 rounded-full bg-gray-600 flex-shrink-0" />
          <input
            ref={addRef}
            value={addText}
            onChange={e => setAddText(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); addItem(); }
              if (e.key === 'Escape') { setAdding(false); setAddText(''); }
            }}
            onBlur={() => { addItem(); setAdding(false); }}
            placeholder="항목 입력..."
            className="flex-1 bg-transparent border-b border-gray-700 text-sm text-white py-0.5 focus:outline-none focus:border-brand placeholder-gray-600"
          />
        </div>
      )}

      {sectionItems.length === 0 && !adding && !canEdit && (
        <p className="text-gray-700 text-xs text-center py-1">없음</p>
      )}
    </div>
  );
}

// ── 메인 ─────────────────────────────────────────────────────────
export default function DailyWorkPage({ department = 'product' }) {
  const { theme } = useTheme();
  const avatarColor = (color) => theme === 'light' ? getContrastColor(color) : 'white';
  const user = JSON.parse(localStorage.getItem('mfg_user') || '{}');
  const isSuperadmin = user.role === 'superadmin' || user.role === 'admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const date = searchParams.get('date') || getKstToday();
  const setDate = (fn) => {
    const next = typeof fn === 'function' ? fn(date) : fn;
    setSearchParams({ date: next }, { replace: true });
  };

  const [connected, setConnected] = useState(false);
  const [peers, setPeers] = useState([]);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searchPage, setSearchPage] = useState(1);
  const [searchAuthorId, setSearchAuthorId] = useState('');
  const [searching, setSearching] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [pendingScrollKeyword, setPendingScrollKeyword] = useState('');
  const fileInputRef = useRef(null);
  const mdFileInputRef = useRef(null);

  const [deptAdmins, setDeptAdmins] = useState([]);
  const [activeSection, setActiveSection] = useState(department === 'product' ? 'section-common' : `section-u${user.id}`);

  // 체크리스트 상태
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showCatModal, setShowCatModal] = useState(false);

  useEffect(() => {
    api.get('/schedule/members').then(r => {
      const deptName = DEPT_MAP[department];
      const filtered = deptName ? r.data.filter(a => a.department === deptName) : r.data;
      setDeptAdmins(filtered);
      const inDept = filtered.some(a => a.id === user.id);
      if (!inDept) setActiveSection('section-common');
    });
  }, [department]);

  const sections = useMemo(() => {
    const ordered = department === 'product'
      ? [...deptAdmins].sort((a, b) => {
          const ai = PRODUCT_SECTION_ORDER.indexOf(a.name);
          const bi = PRODUCT_SECTION_ORDER.indexOf(b.name);
          return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
        })
      : deptAdmins;
    return [
      { key: 'section-common', label: '공통' },
      ...ordered.map(a => ({ key: `section-u${a.id}`, label: a.name || a.username, adminId: a.id, team: a.team })),
    ];
  }, [deptAdmins, department]);

  // 활성 섹션의 adminId
  const activeSectionAdminId = useMemo(() => {
    const s = sections.find(s => s.key === activeSection);
    return s?.adminId || null;
  }, [sections, activeSection]);

  const isPersonSection = activeSection !== 'section-common';

  const canEdit = useMemo(() => {
    if (activeSection === 'section-common') return true;
    if (isSuperadmin) return true;
    return activeSection === `section-u${user.id}`;
  }, [activeSection, isSuperadmin, user.id]);

  // 체크리스트 아이템 로드
  const loadItems = useCallback(() => {
    const params = { date };
    if (activeSectionAdminId) params.adminId = activeSectionAdminId;
    api.get('/daily-work/items', { params })
      .then(r => setItems(r.data))
      .catch(() => {});
  }, [date, activeSectionAdminId]);

  const loadCategories = useCallback(() => {
    const deptName = DEPT_MAP[department];
    api.get('/daily-work/categories', { params: { department: deptName } })
      .then(r => setCategories(r.data))
      .catch(() => {});
  }, [department]);

  useEffect(() => { loadItems(); }, [loadItems]);
  useEffect(() => { loadCategories(); }, [loadCategories]);

  // Yjs (자유 메모)
  const { ydoc, provider } = useMemo(() => {
    const doc = new Y.Doc();
    const token = localStorage.getItem('mfg_token');
    const isDev = import.meta.env.DEV;
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = isDev ? window.location.hostname + ':3800' : window.location.host;
    const prov = new WebsocketProvider(`${wsProtocol}//${wsHost}/yjs`, `daily-${department}-${date}`, doc, { params: { token } });
    return { ydoc: doc, provider: prov };
  }, [date, department]);

  const editorScrollRef = useRef(null);
  useEffect(() => { editorScrollRef.current?.scrollTo({ top: 0 }); }, [date, activeSection]);

  // 검색 결과 클릭 후 키워드로 스크롤
  useEffect(() => {
    if (!pendingScrollKeyword) return;
    const kw = pendingScrollKeyword;
    const tryScroll = (attempt = 0) => {
      const el = editorScrollRef.current?.querySelector('.ProseMirror');
      if (!el) { if (attempt < 15) setTimeout(() => tryScroll(attempt + 1), 200); return; }
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) {
        const node = walker.currentNode;
        const idx = node.textContent.toLowerCase().indexOf(kw.toLowerCase());
        if (idx >= 0) {
          const range = document.createRange();
          range.setStart(node, idx);
          range.setEnd(node, Math.min(node.textContent.length, idx + kw.length));
          const span = document.createElement('span');
          range.surroundContents(span);
          span.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(() => {
            if (span.parentNode) {
              const parent = span.parentNode;
              parent.replaceChild(document.createTextNode(span.textContent), span);
            }
          }, 1500);
          break;
        }
      }
      setPendingScrollKeyword('');
    };
    setTimeout(() => tryScroll(), 400);
  }, [pendingScrollKeyword]);

  useEffect(() => {
    provider.on('status', ({ status }) => setConnected(status === 'connected'));
    if (provider.wsconnected) setConnected(true);

    provider.awareness.setLocalStateField('user', {
      name: user.name || user.username,
      color: getUserColor(user.id || 0),
    });

    const awarenessHandler = () => {
      const seen = new Set();
      const users = [];
      provider.awareness.getStates().forEach((state) => {
        if (state.user && !seen.has(state.user.name)) {
          seen.add(state.user.name);
          users.push({ ...state.user, isMe: false });
        }
      });
      const myName = user.name || user.username;
      if (!seen.has(myName)) {
        users.unshift({ name: myName, color: getUserColor(user.id || 0), isMe: true });
      } else {
        const idx = users.findIndex(u => u.name === myName);
        if (idx >= 0) { users[idx].isMe = true; if (idx > 0) users.unshift(users.splice(idx, 1)[0]); }
      }
      setPeers(users);
    };
    provider.awareness.on('change', awarenessHandler);
    awarenessHandler();

    return () => { provider.destroy(); ydoc.destroy(); };
  }, [provider, ydoc]);

  const YjsExtension = useMemo(() => Extension.create({
    name: 'yjs',
    addProseMirrorPlugins() {
      const fragment = ydoc.getXmlFragment(activeSection);
      return [
        ySyncPlugin(fragment),
        yCursorPlugin(provider.awareness, { cursorBuilder }),
        yUndoPlugin(),
      ];
    },
  }), [ydoc, provider, activeSection]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ history: false }),
      YjsExtension,
      ResizableImage,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Placeholder.configure({ placeholder: '자유 메모를 입력하세요...' }),
      MarkdownPaste,
      Table.configure({ resizable: false }),
      TableRow,
      TableCell,
      TableHeader,
    ],
    editable: canEdit,
    autofocus: false,
    editorProps: {
      attributes: { class: 'prose prose-invert max-w-none focus:outline-none min-h-[200px] px-6 py-4' },
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files;
        if (files?.length) {
          event.preventDefault();
          Array.from(files).forEach(async (f) => {
            if (!f.type.startsWith('image/')) return;
            const url = await uploadImage(f);
            view.dispatch(view.state.tr.replaceSelectionWith(view.state.schema.nodes.image.create({ src: url })));
          });
          return true;
        }
        return false;
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of items) {
          if (item.type.startsWith('image/')) {
            event.preventDefault();
            const file = item.getAsFile();
            if (!file) continue;
            (async () => {
              const url = await uploadImage(file);
              view.dispatch(view.state.tr.replaceSelectionWith(view.state.schema.nodes.image.create({ src: url })));
            })();
            return true;
          }
        }
        return false;
      },
    },
  }, [ydoc, activeSection]);

  useEffect(() => { if (editor) editor.setEditable(canEdit); }, [editor, canEdit]);

  const goDay = (offset) => setDate(d => dayjs(d).add(offset, 'day').format('YYYY-MM-DD'));
  const goToday = () => setDate(getKstToday());

  const doSearch = useCallback(async (page = 1) => {
    if (!search.trim()) return;
    setSearching(true);
    setSearchPage(page);
    try {
      const params = { search: search.trim(), page, limit: 10 };
      if (searchAuthorId) {
        params.authorId = searchAuthorId;
      } else {
        params.department = department;
      }
      const r = await api.get('/daily-work', { params });
      setSearchResults(r.data.items || []);
      setSearchTotal(r.data.total || 0);
    } catch { setSearchResults([]); setSearchTotal(0); }
    setSearching(false);
  }, [search, department, searchAuthorId]);

  const handleImageButton = () => fileInputRef.current?.click();
  const handleMdUpload = () => mdFileInputRef.current?.click();
  const handleMdFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    const text = await file.text();
    const html = markdownToHtml(text);
    const view = editor.view;
    const parser = PMMDOMParser.fromSchema(view.state.schema);
    const dom = document.createElement('div');
    dom.innerHTML = html;
    const slice = parser.parseSlice(dom, { preserveWhitespace: false });
    const tr = view.state.tr.replaceSelection(slice);
    view.dispatch(tr.scrollIntoView());
    e.target.value = '';
  };
  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (!files?.length || !editor) return;
    for (const f of files) {
      if (!f.type.startsWith('image/')) continue;
      const url = await uploadImage(f);
      editor.chain().focus().setImage({ src: url }).run();
    }
    e.target.value = '';
  };

  const kstToday = getKstToday();
  const isToday = date === kstToday;
  const displayDate = dayjs(date);
  const weekdays = ['일', '월', '화', '수', '목', '금', '토'];

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* 헤더 */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div className="flex items-center gap-1">
          <CalendarDays size={20} className="text-brand flex-shrink-0 mr-1" />
          <button onClick={() => goDay(-1)} className="p-1.5 text-gray-400 hover:text-white rounded-full hover:bg-gray-800 transition-colors flex-shrink-0">
            <ChevronLeft size={18} />
          </button>
          <h1 className="text-xl font-bold text-white whitespace-nowrap">
            {displayDate.format('YYYY년 MM월 DD일')} ({weekdays[displayDate.day()]})
          </h1>
          <button onClick={() => goDay(1)} className="p-1.5 text-gray-400 hover:text-white rounded-full hover:bg-gray-800 transition-colors flex-shrink-0">
            <ChevronRight size={18} />
          </button>
          {isToday ? (
            <span className="px-2 py-0.5 text-xs text-brand bg-brand/10 rounded font-medium flex-shrink-0 ml-1">오늘</span>
          ) : (
            <button onClick={goToday} className="px-2 py-1 text-xs text-brand border border-brand/30 rounded hover:bg-brand/10 flex-shrink-0 ml-1">오늘로</button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex -space-x-1.5">
            {peers.slice(0, 4).map((p, i) => (
              <div key={i}
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-2 ${p.isMe ? 'border-brand' : 'border-gray-950'}`}
                style={{ backgroundColor: p.color, color: avatarColor(p.color) }}
                title={p.isMe ? `${p.name} (나)` : p.name}>
                {p.name?.charAt(0)}
              </div>
            ))}
            {peers.length > 4 && <span className="text-gray-500 text-xs ml-1">+{peers.length - 4}</span>}
          </div>
          {!connected && <WifiOff size={14} className="text-red-400" title="연결 끊김" />}
          <button onClick={() => setShowSearch(!showSearch)}
            className={`p-1.5 rounded-lg ${showSearch ? 'bg-brand/20 text-brand' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}>
            <Search size={14} />
          </button>
        </div>
      </div>

      {/* 섹션 탭 */}
      {sections.length > 1 && (
        <div className="flex items-center border-b border-gray-800 bg-gray-950 flex-shrink-0 overflow-x-auto scrollbar-none">
          {sections.map(s => (
            <button key={s.key} onClick={() => setActiveSection(s.key)}
              className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors flex-shrink-0 ${
                activeSection === s.key ? 'text-brand border-brand' : 'text-gray-500 border-transparent hover:text-white hover:border-gray-600'
              }`}>
              {s.label}
              {s.team && <span className="ml-1 text-[10px] opacity-40">{s.team}</span>}
              {s.adminId && s.adminId === user.id && <span className="ml-1 text-[10px] text-brand/60">(나)</span>}
            </button>
          ))}
        </div>
      )}

      {/* 본문 */}
      <div className="flex flex-1 min-h-0">
        <div className="flex-1 flex flex-col min-w-0 overflow-y-auto" ref={editorScrollRef}>

          {/* ── 공통 구조화 필드 ── */}
          {!isPersonSection && (
            <CommonSection date={date} department={DEPT_MAP[department]} canEdit={canEdit} />
          )}

          {/* ── 체크리스트 (개인 섹션만) ── */}
          {isPersonSection && (
            <div className="p-4 border-b border-gray-800/50 flex flex-col gap-3 flex-shrink-0">
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-gray-600 uppercase tracking-widest font-semibold">업무 현황</span>
                {(canEdit || isSuperadmin) && (
                  <button onClick={() => setShowCatModal(true)}
                    className="p-1 text-gray-600 hover:text-gray-300 rounded hover:bg-gray-800" title="카테고리 관리">
                    <Settings size={12} />
                  </button>
                )}
              </div>
              {SECTIONS.map(sec => (
                <CheckSection
                  key={sec.key}
                  sectionDef={sec}
                  items={items}
                  categories={categories}
                  canEdit={canEdit}
                  adminId={activeSectionAdminId}
                  date={date}
                  onItemsChange={setItems}
                />
              ))}
            </div>
          )}

          {/* ── 자유 메모 ── */}
          <div className="flex flex-col flex-1">
            <div className="px-4 py-2 border-b border-gray-800/30 flex items-center justify-between flex-shrink-0">
              <span className="text-[10px] text-gray-600 uppercase tracking-widest font-semibold">자유 메모</span>
              {!canEdit && (
                <span className="text-[10px] text-gray-600">읽기 전용</span>
              )}
            </div>
            {canEdit && <EditorToolbar editor={editor} onImageUpload={handleImageButton} onMarkdownUpload={handleMdUpload} />}
            <div className="flex-1">
              <EditorContent editor={editor} />
            </div>
          </div>

          <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileSelect} />
          <input ref={mdFileInputRef} type="file" accept=".md,text/markdown" className="hidden" onChange={handleMdFile} />
        </div>

        {/* 검색 패널 */}
        {showSearch && (
          <div className="fixed inset-0 z-40 md:relative md:inset-auto md:w-80 md:flex-shrink-0 border-l border-gray-800 flex flex-col bg-gray-950">
            <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between flex-shrink-0">
              <h3 className="text-white text-sm font-semibold flex items-center gap-1.5">
                <Search size={14} /> 내용 검색
              </h3>
              <button onClick={() => setShowSearch(false)} className="text-gray-500 hover:text-white"><X size={14} /></button>
            </div>
            <div className="p-3 border-b border-gray-800 flex flex-col gap-2 flex-shrink-0">
              {/* 작성자 필터 */}
              <select
                value={searchAuthorId}
                onChange={e => setSearchAuthorId(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-gray-300 focus:outline-none focus:border-brand"
              >
                <option value="">전체 작성자</option>
                {deptAdmins.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
              {/* 검색어 입력 */}
              <div className="flex gap-2">
                <input value={search} onChange={e => setSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && doSearch(1)}
                  placeholder="검색어 입력..."
                  className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-brand" />
                <button onClick={() => doSearch(1)} disabled={searching}
                  className="px-3 py-1.5 bg-brand text-white rounded-lg text-sm hover:bg-brand/80 disabled:opacity-50">
                  {searching ? '...' : '검색'}
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {searchResults.length > 0 ? (
                <>
                  <div className="divide-y divide-gray-800/50">
                    {searchResults.map(item => (
                      <button key={item.id} onClick={() => { setDate(item.date); setPendingScrollKeyword(search.trim()); setShowSearch(false); }}
                        className={`w-full text-left px-4 py-3 hover:bg-gray-800/50 transition-colors ${item.date === date ? 'bg-brand/10' : ''}`}>
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-white text-sm font-medium">
                            {dayjs(item.date).format('YYYY.MM.DD')}
                            <span className={`ml-1.5 text-xs ${dayjs(item.date).day() === 0 ? 'text-red-400' : dayjs(item.date).day() === 6 ? 'text-blue-400' : 'text-gray-500'}`}>
                              ({weekdays[dayjs(item.date).day()]})
                            </span>
                          </span>
                          {item.author && <span className="text-xs text-gray-400 flex-shrink-0">{item.author.name}</span>}
                        </div>
                        {item.snippet && (
                          <p className="text-gray-400 text-xs leading-relaxed mt-1 line-clamp-2"
                            dangerouslySetInnerHTML={{
                              __html: item.snippet.replace(
                                new RegExp(`(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'),
                                '<mark style="background:#3b5cf670;color:#93c5fd;border-radius:2px;padding:0 1px">$1</mark>'
                              )
                            }}
                          />
                        )}
                      </button>
                    ))}
                  </div>
                  {/* 페이지네이션 */}
                  {searchTotal > 10 && (
                    <div className="flex items-center justify-between px-4 py-2 border-t border-gray-800 flex-shrink-0">
                      <span className="text-xs text-gray-500">총 {searchTotal}건</span>
                      <div className="flex items-center gap-1">
                        <button onClick={() => doSearch(searchPage - 1)} disabled={searchPage <= 1 || searching}
                          className="p-1 rounded hover:bg-gray-800 text-gray-500 disabled:opacity-30"><ChevronLeft size={13} /></button>
                        <span className="text-xs text-gray-400 min-w-[48px] text-center">
                          {searchPage} / {Math.ceil(searchTotal / 10)}
                        </span>
                        <button onClick={() => doSearch(searchPage + 1)} disabled={searchPage >= Math.ceil(searchTotal / 10) || searching}
                          className="p-1 rounded hover:bg-gray-800 text-gray-500 disabled:opacity-30"><ChevronRight size={13} /></button>
                      </div>
                    </div>
                  )}
                </>
              ) : search && !searching ? (
                <p className="text-gray-600 text-xs text-center py-8">검색 결과가 없습니다.</p>
              ) : (
                <p className="text-gray-600 text-xs text-center py-8">검색어를 입력하고 Enter를 누르세요.</p>
              )}
            </div>
          </div>
        )}
      </div>

      {showCatModal && (
        <CategoryModal
          categories={categories}
          department={DEPT_MAP[department]}
          onClose={() => setShowCatModal(false)}
          onRefresh={loadCategories}
        />
      )}
    </div>
  );
}
